import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  ChartBarGradient,
  ChartEmptyState,
  ChartPanel,
} from '../../components/stats/ChartPrimitives';
import { chartGradientFill, useChartGradientId } from '../../components/stats/chartGradient';
import {
  CHART_AXIS_PROPS,
  CHART_AXIS_TICK,
  CHART_BAR_PROPS,
  CHART_ERROR,
  CHART_GRID_PROPS,
  CHART_SUCCESS,
  CHART_SUCCESS_LIGHT,
  CHART_TOOLTIP_LABEL_STYLE,
  CHART_TOOLTIP_STYLE,
} from '../../components/stats/chartTheme';

interface RequestVolumePoint {
  label: string;
  success: number;
  error: number;
}

interface RequestVolumeChartProps {
  title: string;
  data: RequestVolumePoint[];
  total: number;
  loading: boolean;
  loadingMessage: string;
  emptyMessage: string;
}

const REQUEST_ICON = (
  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 18V9m5 9V5m5 13v-7m5 7V3" />
  </svg>
);

export function RequestVolumeChart({
  title,
  data,
  total,
  loading,
  loadingMessage,
  emptyMessage,
}: RequestVolumeChartProps) {
  const successGradientId = useChartGradientId('request-volume-success');
  const errorGradientId = useChartGradientId('request-volume-error');

  return (
    <ChartPanel
      title={title}
      icon={REQUEST_ICON}
      iconClassName="bg-success-50 text-success-700"
      summary={
        <div className="text-right">
          <span className="text-lg font-semibold text-slate-700">{total}</span>
          <span className="ml-1 text-[10px] uppercase text-slate-500">total</span>
        </div>
      }
    >
      <div className="mb-2 flex justify-end gap-4">
        <span className="inline-flex items-center gap-1.5 text-[10px] text-slate-500">
          <span className="h-1.5 w-1.5 rounded-full bg-success-600" />
          Success
        </span>
        <span className="inline-flex items-center gap-1.5 text-[10px] text-slate-500">
          <span className="h-1.5 w-1.5 rounded-full bg-danger-500" />
          Error
        </span>
      </div>

      <div className="flex h-64">
        {loading && total === 0 ? (
          <ChartEmptyState>{loadingMessage}</ChartEmptyState>
        ) : total > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data}
              margin={{ top: 4, right: 8, left: -16, bottom: 0 }}
              barCategoryGap="18%"
            >
              <defs>
                <ChartBarGradient
                  id={successGradientId}
                  color={CHART_SUCCESS}
                  lightColor={CHART_SUCCESS_LIGHT}
                />
                <ChartBarGradient id={errorGradientId} color={CHART_ERROR} />
              </defs>
              <CartesianGrid {...CHART_GRID_PROPS} />
              <XAxis
                {...CHART_AXIS_PROPS}
                dataKey="label"
                tick={CHART_AXIS_TICK}
                interval="preserveStartEnd"
                minTickGap={24}
              />
              <YAxis {...CHART_AXIS_PROPS} allowDecimals={false} tick={CHART_AXIS_TICK} />
              <Tooltip
                contentStyle={CHART_TOOLTIP_STYLE}
                labelStyle={CHART_TOOLTIP_LABEL_STYLE}
              />
              <Bar
                {...CHART_BAR_PROPS}
                dataKey="success"
                name="Success"
                stackId="a"
                fill={chartGradientFill(successGradientId)}
                isAnimationActive={false}
              />
              <Bar
                {...CHART_BAR_PROPS}
                dataKey="error"
                name="Error"
                stackId="a"
                fill={chartGradientFill(errorGradientId)}
                radius={[2, 2, 0, 0]}
                isAnimationActive={false}
              />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <ChartEmptyState>{emptyMessage}</ChartEmptyState>
        )}
      </div>
    </ChartPanel>
  );
}
